'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import {
  appCookie,
  appLocalStorage,
  cartAllProductsSelector,
  cartTotalCountSelector,
  clearUserAuthToken,
  cn,
  IS_SSO_AUTH,
  logoutAccount,
  pageIdSelector,
  Price,
  setLocked,
  sleep,
  storeConfigSelector,
  // TCustomerCartProductItem,
  useBroadcastChannel,
  useLoadingContext,
  useMediaQuery,
  userIsLoggedInSelector,
  useSignIn,
} from '@segway/core'
import type { GetFormilyBlockQuery } from '@segway/core/src/graphql/generated/graphql'
import {
  useGetFormilyBockQuery,
  useGetMegaMenuQuery,
  useRevokeCustomerTokenMutation,
} from '@segway/core/src/services'
import { useAppDispatch, useAppSelector } from '@segway/core/src/store/hooks'
import { useClickAway } from 'ahooks'
import throttle from 'lodash/throttle'

import { Carousel, CarouselItem, EmptyIcon, LoginPromptMobile } from '@/components'
import {
  HambergerIcon,
  LogoMbIcon,
  LogoPcIcon,
  MenuCloseIcon,
  Search,
  SearchClearIcon,
  SearchIconPcIcon,
  ShoppingCart,
  User,
} from '@/components/icons'
import { Link, usePathname, useRouter } from '@/i18n/navigation'
import {
  Button,
  // DropdownMenu,
  // DropdownMenuContent,
  // DropdownMenuItem,
  // DropdownMenuTrigger,
  Input,
} from '@/ui'

import QuickLink from '../quickLink/QuickLink'

// import { Popover, PopoverContent, PopoverTrigger } from '@/ui'
// import { Arrow, Close, Line } from '@/components/icons'
import HeaderTop from './HeaderTop'

import styles from './header.module.css'

const Header = () => {
  const t = useTranslations('Common')
  const tCart = useTranslations('Cart')
  const tLogin = useTranslations('Login')

  const loading = useLoadingContext()
  const { postMessage } = useBroadcastChannel('global')

  const { ssoLogout } = useSignIn()
  const [revokeCustomerTokenMutation] = useRevokeCustomerTokenMutation()

  const pageId = useAppSelector(pageIdSelector)
  const [isClient, setIsClient] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [open, setOpen] = useState(false)
  const [isNavFixed, setIsNavFixed] = useState(false)
  const [isSearch, setIsSearch] = useState(false)
  const responsive = useMediaQuery()
  const pathName = usePathname()
  const { data } = useGetMegaMenuQuery({})
  const { data: homeTopData } = useGetFormilyBockQuery({
    identify: 'homepage_top_links',
  })
  const router = useRouter()
  const dispatch = useAppDispatch()
  const isLoggedIn = useAppSelector(userIsLoggedInSelector)
  const cartTotalCount = useAppSelector(cartTotalCountSelector)
  const cartAllProducts = useAppSelector(cartAllProductsSelector)
  const [accountUrl, setAccountUrl] = useState('')
  const [registerUrl, setRegisterUrl] = useState('')
  const [accountOpen, setAccountOpen] = useState(false)
  const [minicatOpen, setMinicatOpen] = useState(false)
  const [promotionOpen, setPromotionOpen] = useState(false)
  const [isScroll, setIsScroll] = useState(false)
  const [menuTop, setMenuTop] = useState(0)
  const navRef = useRef<HTMLDivElement>(null)

  const storeConfig = useAppSelector(storeConfigSelector)

  const menuData = useMemo(() => {
    if (data && data.categories) {
      const categories = data.categories?.items
      if (categories && categories[0] && categories[0]?.children) {
        return categories[0].children.filter((child) => child && child.include_in_menu)
      } else {
        return []
      }
    } else {
      return []
    }
  }, [data])

  const headOptionsUrl = useMemo(() => {
    return ['/', '/affiliate-program-1', '/affiliate-program']
  }, [])

  const isShowLoginPrompt = useMemo(() => {
    return responsive && !responsive.xl && ['/checkout/cart', '/checkout'].includes(pathName)
  }, [responsive, pathName])

  const homeTopRightDatas = useMemo(() => {
    if (homeTopData?.formilyBlock?.block_data) {
      const parseData =
        homeTopData?.formilyBlock?.block_data && JSON.parse(homeTopData?.formilyBlock?.block_data)
      return parseData
    }
  }, [homeTopData])

  const searchFormRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLButtonElement>(null)
  useClickAway(() => {
    setOpen(false)
    setIsSearch(false)
  }, [searchRef, searchFormRef])

  const miniCartRef = useRef<HTMLDivElement>(null)
  const miniCartBtnRef = useRef<HTMLButtonElement>(null)
  useClickAway(() => {
    setMinicatOpen(false)
  }, [miniCartRef, miniCartBtnRef])

  const accountRef = useRef<HTMLButtonElement>(null)
  useClickAway(() => {
    setAccountOpen(false)
  }, accountRef)

  const handSubmit = () => {}

  const menuHeightCalc = () => {
    const promotionH = document.getElementById('header-promotion')?.clientHeight
    const navH = navRef?.current?.clientHeight
    if (responsive && !responsive.xl) {
      const loginMsgH = document.getElementById('login-prompt')?.clientHeight
      if (loginMsgH) {
        if (isScroll) {
          if (navH) {
            return navH
          }
        }

        if (!homeTopData || !promotionOpen) {
          if (navH) {
            return navH + loginMsgH
          }
        }

        if (navH && promotionH) {
          return navH + promotionH + loginMsgH
        }
      } else {
        if (isScroll || !homeTopData || !promotionOpen) {
          if (navH) {
            return navH - 2
          }
        }

        if (navH && promotionH) {
          return navH + promotionH - 2
        }
      }
    }

    return navH
  }

  // 切换菜单的显示状态
  const toggleMenu = () => {
    if (isOpen) {
      document.documentElement.removeAttribute('style')
    } else {
      // 打开菜单时，锁定页面滚动
      document.documentElement.style.overflow = 'hidden'
      const menuTop = menuHeightCalc()
      if (menuTop) {
        setMenuTop(menuTop)
      }
      const el = document.querySelector('#menuNav')
      setTimeout(() => {
        if (el) {
          el.scrollTop = 0
        }
      }, 100)
    }

    setIsOpen(!isOpen)
    setIsSearch(false)
  }

  const handleToggleSearch = useCallback(() => {
    setIsSearch((prev) => !prev)

    setIsOpen(false) // 菜单打开或关闭
    setMinicatOpen(false)
    setAccountOpen(false)
    document.documentElement.removeAttribute('style')
  }, [])

  useEffect(() => {
    if (isLoggedIn) {
      setAccountUrl('')
      setRegisterUrl('')
    } else {
      setRegisterUrl('/register')
      if (/^\/checkout$/.test(pathName)) {
        setAccountUrl('/checkout/cart')
      } else if (
        !/sign-in|account-binding|old-sign-in|register|success|sso-auth|forgot-password/.test(
          pathName,
        )
      ) {
        setAccountUrl('/sign-in?callbackUrl=' + encodeURIComponent(window.location.href))
      } else {
        setAccountUrl('/sign-in')
      }
    }
  }, [isLoggedIn, pathName])

  useEffect(() => {
    if (pathName && headOptionsUrl.includes(pathName)) {
      if (document.documentElement.scrollTop > 48) {
        setIsNavFixed(true)
      } else {
        setIsNavFixed(false)
      }

      const handleScroll = () => {
        if (document.documentElement.scrollTop > 48) {
          setIsNavFixed(true)
        } else {
          setIsNavFixed(false)
        }

        // 滚动就关闭
        setMinicatOpen(false)
        setAccountOpen(false)
        setOpen(false)
        setIsSearch(false)
      }

      // 添加事件监听器
      window.addEventListener('scroll', handleScroll)

      // 组件卸载时移除监听器
      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    } else {
      setIsNavFixed(true)
    }

    // 解开锁住
    document.documentElement.removeAttribute('style')

    return () => {
      document.documentElement.removeAttribute('style')
    }
  }, [pathName, headOptionsUrl])

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (responsive && responsive.xl && isOpen) {
      setIsOpen(false)
    }
  }, [isOpen, responsive])

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (document.documentElement.scrollTop > 48) {
        setIsScroll(true)
      } else {
        setIsScroll(false)
      }
    }, 50)

    // 添加事件监听器
    window.addEventListener('scroll', handleScroll)

    // 组件卸载时移除监听器
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const backdropBlurDivTopValue = () => {
    const promotionH = document.getElementById('header-promotion')?.clientHeight
    const navH = navRef?.current?.clientHeight
    if (responsive && responsive.xl) {
      if (isScroll || !homeTopData) {
        if (navH) {
          return `${navH + 84}px`
        }
      }

      if (promotionH && navH) {
        return `${promotionH + navH + 84}px`
      }
    } else {
      const loginMsgH = document.getElementById('login-prompt')?.clientHeight
      if (loginMsgH) {
        if (isScroll) {
          if (navH) {
            return `${navH + 40}px`
          }
        }

        if (!homeTopData || !promotionOpen) {
          if (navH) {
            return `${navH + loginMsgH + 40}px`
          }
        }

        if (promotionH && navH) {
          return `${loginMsgH + promotionH + navH + 40}px`
        }
      } else {
        if (isScroll || !homeTopData || !promotionOpen) {
          if (navH) {
            return `${navH + 40}px`
          }
        }

        if (promotionH && navH) {
          return `${promotionH + navH + 40}px`
        }
      }
    }

    return 0
  }

  /**
   * 退出账户状态，广播到其他标签页
   */
  const handleLogoutAccount = useCallback(async () => {
    loading.show()
    postMessage({
      type: setLocked.type,
      pageId,
      payload: true,
    })

    const isSSOAuth = appLocalStorage.getItemSync(IS_SSO_AUTH)
    if (isSSOAuth === '1') {
      const response = await ssoLogout()
      if (response && response.success) {
        appLocalStorage.removeItemSync(IS_SSO_AUTH)
        dispatch(logoutAccount())
          .then(async () => {
            postMessage({
              type: clearUserAuthToken.type,
              pageId,
              payload: '',
            })
          })
          .finally(async () => {
            appLocalStorage.clear()
            appCookie.clear()
            await sleep(500)
            if (pathName.startsWith('/customer')) {
              // 退出登录后跳转到首页
              window.location.href = `${response.logout_url}?referer=${encodeURIComponent(window.location.origin)}`
            } else {
              window.location.href = `${response.logout_url}?referer=${encodeURIComponent(window.location.href)}`
            }
          })
      }
    } else {
      await revokeCustomerTokenMutation({})
      dispatch(logoutAccount())
        .then(async () => {
          postMessage({
            type: clearUserAuthToken.type,
            pageId,
            payload: '',
          })
        })
        .finally(async () => {
          appLocalStorage.clear()
          appCookie.clear()
          await sleep(500)
          if (pathName.startsWith('/customer')) {
            // 退出登录后跳转到首页
            window.location.href = '/'
          } else {
            loading.hide()
            window.location.reload()
          }
        })
    }
  }, [dispatch, postMessage, loading, pageId, pathName, ssoLogout, revokeCustomerTokenMutation])

  // const isOutOfStock = (productItem: { rawProduct: TCustomerCartProductItem }) => {
  //   if (productItem.rawProduct.__typename === 'BundleCartItem') {
  //     // bundle product 产品判断主品
  //     const requiredBundleProduct = productItem.rawProduct.bundle_options.find(
  //       (option) => option?.required,
  //     )

  //     if (requiredBundleProduct) {
  //       return requiredBundleProduct.values.every((option) => {
  //         return option?.saleable_stock === 0
  //       })
  //     }

  //     return true
  //   }

  //   if (productItem.rawProduct.product.saleable_stock) {
  //     return productItem.rawProduct.product.saleable_stock === 0
  //   }

  //   return true
  // }

  const getMincartWidth = useMemo(() => {
    if (cartTotalCount < 10) {
      return 14
    } else if (cartTotalCount > 99) {
      return 26
    } else {
      return 20
    }
  }, [cartTotalCount])

  const stylesInline = `
    <style>
      :root {
          --theme-player-z-index: 1;
      }
    </style>
  `

  useEffect(() => {
    if (isSearch) {
      document.body.setAttribute('data-scroll-locked', '1')
    } else {
      document.body.removeAttribute('data-scroll-locked')
    }
  }, [isSearch])

  return isClient ? (
    <>
      {isOpen ? <div dangerouslySetInnerHTML={{ __html: stylesInline }}></div> : null}
      <HeaderTop
        homeTopDatas={homeTopData as GetFormilyBlockQuery}
        setPromotionOpen={setPromotionOpen}
        promotionOpen={promotionOpen}
      />
      <div id="header-holder"></div>
      {isShowLoginPrompt && <LoginPromptMobile text={tCart('login_notice')} />}
      <div
        id="page-nav"
        className={`${isScroll ? 'fixed' : 'sticky'} top-0 z-30 w-full ${isNavFixed || !headOptionsUrl.includes(pathName) ? 'bg-white' : ''} transition-bg px-[24px] duration-200 max-xl:justify-normal max-xl:px-4 ${isOpen ? 'bg-white' : ''} ${open || isSearch ? 'bg-white' : ''}`}
        onMouseEnter={() => setIsNavFixed(true)}
        onMouseLeave={() => {
          if (pathName == '/') {
            if (document.documentElement.scrollTop > 48) {
              setIsNavFixed(true)
            } else {
              setIsNavFixed(false)
            }
          }

          if (minicatOpen || accountOpen) {
            setMinicatOpen(false)
            setAccountOpen(false)
          }
        }}>
        <div
          className={`fixed bottom-0 left-0 right-0 backdrop-blur-[10px] ${isSearch ? 'block' : 'hidden'}`}
          style={{
            top: backdropBlurDivTopValue(),
          }}></div>
        <div id="header-nav" ref={navRef} className="relative">
          {/* 导航栏 */}
          <div
            className={`relative flex h-[40px] w-full items-center justify-between text-white xl:h-[52px] xl:text-[12px] 2xl:max-w-[1440px] 2xl:text-[14px] ${styles.header} mx-auto xl:gap-11`}>
            {/* 左侧logo */}
            <div className="flex items-center justify-center gap-x-3">
              <Link href={'/'}>
                {responsive && responsive.xl ? (
                  <LogoPcIcon
                    width={responsive && responsive['2xl'] ? 173 : 121}
                    height={responsive && responsive['2xl'] ? 24 : 16}
                    color={isNavFixed || open || isSearch ? '#000' : '#fff'}
                  />
                ) : (
                  <LogoMbIcon color={isNavFixed || open || isOpen || isSearch ? '#000' : '#fff'} />
                )}
              </Link>
            </div>

            {/* pc端中间items */}
            <div className="hidden items-center overflow-hidden xl:flex">
              <Carousel
                autoPlay={false}
                showIndicators={false}
                showArrows={responsive && responsive.xl ? true : false}
                arrowIconDisableClassName="hidden"
                className="w-full"
                uiCarouselClassName="px-10"
                arrowLeftContainerClassName="w-auto h-auto left-0 bg-transparent active:bg-transparent focus:bg-transparent hover:bg-transparent"
                arrowRightContainerClassName="w-auto h-auto right-0 bg-transparent active:bg-transparent focus:bg-transparent hover:bg-transparent"
                arrowLeftIconColor={isNavFixed || open || isSearch ? '#000' : '#fff'}
                arrowRightIconColor={isNavFixed || open || isSearch ? '#000' : '#fff'}
                arrowHoverOption={{ iconColor: '#000' }}
                options={{ dragFree: responsive && responsive.xl ? false : true }}>
                {menuData?.map((item, index) => {
                  let isTarget = false

                  const openWindowState = item?.formily_content?.find(
                    (content) => content?.code === 'open_in_new_window',
                  )

                  if (item?.url_key?.includes('http://') || item?.url_key?.includes('https://')) {
                    isTarget = true
                  }

                  if (openWindowState && openWindowState.data === '1') {
                    isTarget = true
                  }

                  const url =
                    item?.url_key?.includes('http://') || item?.url_key?.includes('https://')
                      ? item?.url_key
                      : `/${item?.url_key}`

                  return (
                    <CarouselItem
                      key={index}
                      className={`w-fit basis-auto last:mr-0 xl:mr-[16px] 2xl:mr-[36px]`}>
                      <Link
                        key={index}
                        href={url}
                        target={isTarget ? '_blank' : '_self'}
                        className={cn(
                          'font-miSansDemiBold450 leading-[1.6rem] text-white hover:text-primary xl:text-[12px] 2xl:text-base',
                          {
                            'text-black': isNavFixed || open || isSearch,
                          },
                        )}>
                        {item?.name}
                      </Link>
                    </CarouselItem>
                  )
                })}
              </Carousel>
            </div>

            {/* 右侧图标 */}
            <div className="relative flex gap-[24px] text-[0px] xl:gap-[12px] 2xl:gap-[32px]">
              <button
                className="h-[20px]"
                ref={searchRef}
                onClick={handleToggleSearch}
                onMouseOver={() => {
                  setMinicatOpen(false)
                  setAccountOpen(false)
                }}>
                <Search
                  width={responsive && responsive.xl ? 20 : 24}
                  height={responsive && responsive.xl ? 20 : 24}
                  color={isNavFixed || isOpen || isSearch || open ? '#000' : '#fff'}
                />
              </button>
              <div>
                <button
                  className="relative"
                  ref={miniCartBtnRef}
                  onMouseOver={() => {
                    if (globalThis && globalThis.innerWidth >= 1200) {
                      if (cartTotalCount > 0) {
                        setMinicatOpen(true)
                        setAccountOpen(false)
                        setOpen(false)
                        setIsSearch(false)
                      }
                    }
                  }}
                  onClick={() => {
                    if (globalThis && globalThis.innerWidth < 1200) {
                      setAccountOpen(false)
                      setOpen(false)
                      setIsSearch(false)
                      setIsOpen(false)
                      document.documentElement.removeAttribute('style')
                      router.push('/checkout/cart')
                    } else {
                      router.push('/checkout/cart')
                    }
                  }}>
                  <ShoppingCart
                    width={responsive && responsive.xl ? 20 : 24}
                    height={responsive && responsive.xl ? 20 : 24}
                    color={isNavFixed || isOpen || isSearch || open ? '#000' : '#fff'}
                  />
                  {cartTotalCount > 0 ? (
                    <span
                      className={`leading-1 absolute ${cartTotalCount > 99 ? 'right-[-12px]' : 'right-[-6px]'} top-[-4px] flex h-[14px] min-w-[8px] items-center justify-center whitespace-nowrap rounded-[20px] bg-[#DA291C] px-[3px] text-[12px] text-[#fff]`}
                      style={{ width: `${getMincartWidth}px` }}>
                      {cartTotalCount > 99 ? '99+' : cartTotalCount}
                    </span>
                  ) : null}
                </button>
                <div
                  ref={miniCartRef}
                  className={`absolute right-0 top-[36px] z-20 ${minicatOpen ? '' : 'hidden'}`}
                  onMouseLeave={() => setMinicatOpen(false)}>
                  <div className="w-[320px] rounded-[8px] border-[1px] border-[#F3F3F4] bg-[#fff] py-[20px] pl-[16px] pr-[10px] text-[#000] backdrop-blur-[24px]">
                    <div className="grid max-h-[262px] gap-[4px] overflow-y-auto">
                      {cartTotalCount > 0 ? null : (
                        <div className="flex justify-center">
                          <EmptyIcon width={120} height={120} />
                        </div>
                      )}
                      {cartAllProducts.map((item) => (
                        <Link
                          href={`/${item.rawProduct.product.url_key}`}
                          className="mr-[10px] flex items-center rounded-[8px] py-[6px] hover:bg-[#F3F3F4]"
                          key={item.uid}>
                          <div className="relative h-[64px] w-[64px]">
                            {item.rawProduct.product.image?.url && (
                              <Image
                                src={item.rawProduct.product.image?.url}
                                alt={item.rawProduct.product.image?.label ?? ''}
                                fill
                                loading="lazy"
                                style={{ objectFit: 'contain' }}
                              />
                            )}
                            {/* {isOutOfStock(item) && (
                              <div className="absolute bottom-0 left-0 right-0 bg-gray-8/20 py-1 text-center text-sm text-white">
                                <span>{t('out_of_stock')}</span>
                              </div>
                            )} */}
                          </div>
                          <div className="flex-1 px-[16px]">
                            <div className="line-clamp-2 font-miSansDemiBold450 text-base text-black">
                              {item.rawProduct.product.name}
                            </div>
                            <div className="mt-[6px] flex items-center">
                              {/* <span className="mr-[8px] font-[14px] text-[#86868B]">
                                {item.quantity}x
                              </span> */}
                              <span className="font-[14px] text-[#86868B]">
                                {!storeConfig?.is_price_include_tax &&
                                  item.rawProduct.prices?.row_total && (
                                    <div className="flex items-center space-x-1">
                                      <Price
                                        price={item.rawProduct.prices.row_total}
                                        currencyStyle="text-base text-[14px] text-[#86868B]"
                                        textStyle="text-base text-[14px] text-[#86868B]"
                                        fractionStyle="text-base text-[14px] text-[#86868B]"
                                      />
                                    </div>
                                  )}
                                {storeConfig?.is_price_include_tax &&
                                  item.rawProduct.prices?.row_total_including_tax && (
                                    <div className="flex items-center space-x-1">
                                      <Price
                                        price={item.rawProduct.prices.row_total_including_tax}
                                        currencyStyle="text-base text-[14px] text-[#86868B]"
                                        textStyle="text-base text-[14px] text-[#86868B]"
                                        fractionStyle="text-base text-[14px] text-[#86868B]"
                                      />
                                      <span className="text-[12px] text-base text-[#86868B]">
                                        ({tCart('vat_included')})
                                      </span>
                                    </div>
                                  )}
                              </span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className="mt-[14px] border-t-[1px] border-[#F3F3F4] pt-[20px]">
                      <Button
                        className="w-[100%]"
                        onClick={() => {
                          router.push('/checkout/cart')
                        }}>
                        {t('view_cart')}{' '}
                        {cartTotalCount > 99
                          ? '(99+)'
                          : cartTotalCount > 0
                            ? `(${cartTotalCount})`
                            : ''}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {accountUrl && !isLoggedIn ? (
                <>
                  {responsive && responsive.xl ? (
                    <>
                      <button
                        ref={accountRef}
                        className="hidden cursor-pointer xl:block"
                        onMouseOver={() => {
                          setAccountOpen(true)
                          setMinicatOpen(false)
                          setOpen(false)
                          setIsOpen(false)
                          setIsSearch(false)
                        }}>
                        <User
                          width={responsive && responsive.xl ? 20 : 24}
                          height={responsive && responsive.xl ? 20 : 24}
                          color={isNavFixed || isOpen || isSearch || open ? '#000' : '#fff'}
                        />
                      </button>
                      <div
                        className={`absolute right-0 top-[36px] z-20 ${accountOpen ? '' : 'hidden'} max-xl:hidden`}>
                        <div
                          className="w-[110px] space-y-[12px] rounded-[8px] border-[1px] border-[#F3F3F4] bg-[#fff] px-[16px] py-[20px] backdrop-blur-[24px]"
                          onMouseLeave={() => setAccountOpen(false)}>
                          {[
                            {
                              label: tLogin('sign_in'),
                              href: accountUrl,
                              key: 'sign-in',
                            },
                            {
                              label: tLogin('sign_up'),
                              href: registerUrl,
                              key: 'sign-up',
                            },
                          ].map((item, index) => (
                            <div className="" key={index}>
                              <div
                                className="block cursor-pointer text-[12px] text-[#000] hover:text-primary"
                                onClick={() => {
                                  setAccountOpen(false)

                                  if (item.key === 'sign-in') {
                                    router.push(item.href)
                                  }

                                  if (item.key === 'sign-up') {
                                    window.location.href = item.href
                                  }
                                }}>
                                {item.label}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      href={accountUrl}
                      onClick={() => {
                        setAccountOpen(false)
                        setMinicatOpen(false)
                        setOpen(false)
                        setIsOpen(false)
                        setIsSearch(false)
                      }}>
                      <User
                        width={responsive && responsive.xl ? 20 : 24}
                        height={responsive && responsive.xl ? 20 : 24}
                        color={isNavFixed || isOpen || isSearch || open ? '#000' : '#fff'}
                      />
                    </Link>
                  )}
                </>
              ) : (
                <div>
                  <button
                    ref={accountRef}
                    className="hidden cursor-pointer xl:block"
                    onMouseOver={() => {
                      setAccountOpen(true)
                      setMinicatOpen(false)
                      setOpen(false)
                      setIsSearch(false)
                    }}>
                    <User
                      width={responsive && responsive.xl ? 20 : 24}
                      height={responsive && responsive.xl ? 20 : 24}
                      color={isNavFixed || isOpen || isSearch || open ? '#000' : '#fff'}
                    />
                  </button>
                  <div
                    className={`absolute right-0 top-[36px] z-20 ${accountOpen ? '' : 'hidden'} max-xl:hidden`}>
                    <div
                      className="w-[102px] rounded-[8px] border-[1px] border-[#F3F3F4] bg-[#fff] px-[16px] py-[20px] backdrop-blur-[24px]"
                      onMouseLeave={() => setAccountOpen(false)}>
                      {[
                        {
                          label: t('order'),
                          href: '/customer/sales/order/history',
                        },
                        {
                          label: t('account'),
                          href: '/customer/account',
                        },
                      ].map((item, index) => (
                        <div className="" key={index}>
                          <Link
                            href={item.href}
                            className="block pb-[12px] text-[12px] text-[#000] hover:text-primary"
                            onClick={() => {
                              setAccountOpen(false)
                            }}>
                            {item.label}
                          </Link>
                        </div>
                      ))}
                      <div
                        className="block text-[12px] text-[#000] hover:text-primary"
                        role="button"
                        tabIndex={0}
                        onClick={handleLogoutAccount}>
                        {t('logout')}
                      </div>
                    </div>
                  </div>
                  <Link
                    href={'/customer/account'}
                    className="hidden max-xl:block"
                    onClick={() => {
                      setAccountOpen(false)
                      setMinicatOpen(false)
                      setOpen(false)
                      setIsOpen(false)
                      setIsSearch(false)
                      document.documentElement.removeAttribute('style')
                    }}>
                    <User
                      width={responsive && responsive.xl ? 20 : 24}
                      height={responsive && responsive.xl ? 20 : 24}
                      color={isNavFixed || isOpen || isSearch || open ? '#000' : '#fff'}
                    />
                  </Link>
                </div>
              )}
              <div className="flex xl:hidden" onClick={toggleMenu}>
                <span className={`${isOpen ? '' : 'hidden'}`}>
                  <MenuCloseIcon color="#000" />
                </span>
                <span className={`${isOpen ? 'hidden' : ''}`}>
                  <HambergerIcon
                    color={isNavFixed || isOpen || isSearch || open ? '#000' : '#fff'}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* mobile端菜单 */}
        <div
          className={`overflow-hidden transition-all duration-300 ${isOpen ? styles.memuOpen : 'hidden'}`}>
          <div
            className={`w-full bg-white ${styles.memuContent}`}
            id="menuNav"
            style={{
              top: `${menuTop}px`,
            }}>
            <div className="pb-[40px]">
              <ul>
                {menuData?.map((item, index) => (
                  <li key={index}>
                    <Link
                      href={`/${item?.url_key}`}
                      className="flex items-center px-[14px] py-4 hover:bg-gray-100">
                      <div className="h-[32px] text-xl">
                        <Image
                          src={`${item?.menu_image}`}
                          alt={`${item?.meta_title || ''}`}
                          width={32}
                          height={32}
                        />
                      </div>
                      <span className="ml-[12px] text-xl leading-[22px]">{item?.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <div className={`${styles.lineBox}`}>
                <div className={`${styles.line}`}></div>
              </div>
              <ul>
                {homeTopRightDatas?.links?.map(
                  (item: { title: string; link: string }, index: number) => (
                    <li key={index}>
                      <Link
                        href={item?.link}
                        className="flex h-[64px] items-center p-4 text-[18px] hover:bg-gray-100">
                        {item?.title}
                      </Link>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* 搜索框 */}
        <div
          ref={searchFormRef}
          className={`absolute left-0 grid w-full overflow-hidden bg-white px-7 transition-all duration-300 ${isSearch ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'} max-xl:px-4`}>
          <div className="mx-auto min-h-0 w-full overflow-hidden xl:max-w-[1440px]">
            <form
              className="relative py-5 max-xl:flex max-xl:items-center max-xl:py-1"
              onSubmit={handSubmit}
              action={'/catalog/search'}
              method="get">
              <button type="submit" className="absolute left-1 top-9 z-[1] max-xl:hidden">
                <SearchIconPcIcon color={'#0000004D'} />
              </button>
              <button type="submit" className="absolute left-3 top-[13px] z-[1] xl:hidden">
                <SearchIconPcIcon width={16} height={16} color={'#0000004D'} />
              </button>
              <button type="reset" className="absolute right-[8.2rem] top-[13px] z-[1] xl:hidden">
                <SearchClearIcon width={16} height={16} />
              </button>
              <div className="flex-1">
                <Input
                  placeholder={t('search_placeholder')}
                  name="q"
                  type="text"
                  autoComplete="off"
                  className="h-[48px] w-full border-x-0 border-b border-t-0 border-b-[#0000001A] px-[30px] py-2 placeholder:text-[#00000080] focus-visible:outline-none max-xl:h-[36px] max-xl:rounded-full max-xl:border-none max-xl:bg-[#F0F1F2] max-xl:px-9 xl:rounded-none"
                />
              </div>
              <Button
                type="button"
                variant={'ghost'}
                className="ml-3 h-auto p-0 font-miSansRegular330 text-base text-[#0000004D] hover:bg-transparent xl:hidden"
                onClick={() => setIsSearch(false)}>
                {t('cancel')}
              </Button>
            </form>
            <QuickLink />
          </div>
        </div>
      </div>
    </>
  ) : null
}

export default Header
